import { Routes } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { DashboardComponent } from './components/private/pages/dashboard/dashboard.component';
import { HomepageComponent } from './components/public/homepage/homepage.component';
import { LayoutComponent } from './components/public/layout/layout.component';
import { StepperComponent } from './components/public/stepper/stepper.component';
import { AuthGuard } from './guards/auth.guard';
import { LoggedInGuard } from './guards/loggedIn.guard';
import { MobileGuard } from './guards/mobile.guard';
import { RegistrationGuard } from './guards/registration.guards';
import { StepperGuard } from './guards/stepper.guard';

export const buildRoutes = async (transloco: TranslocoService): Promise<Routes> => {
  try {
    const routesTranslations = await firstValueFrom(transloco.selectTranslateObject('routes'));

    const t = (key: string): string => routesTranslations[key] || key;

    return [
      {
        path: '',
        component: LayoutComponent,
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./components/public/homepage/homepage.module').then((m) => m.HomepageModule),
            canActivate: [LoggedInGuard, MobileGuard],
          },
          {
            path: 'verify',
            component: HomepageComponent,
            data: { expectedQueryParams: ['token'] },
          },

          // ROUTES STATICHE IT / EN per SSR
          {
            path: 'chi-siamo',
            loadChildren: () =>
              import('./components/public/about/about.module').then((m) => m.AboutModule),
          },
          {
            path: 'about-us',
            loadChildren: () =>
              import('./components/public/about/about.module').then((m) => m.AboutModule),
          },
          {
            path: 'come-funziona',
            loadChildren: () =>
              import('./components/public/howitworks/howitworks.module').then(
                (m) => m.HowitworksModule,
              ),
          },
          {
            path: 'how-it-works',
            loadChildren: () =>
              import('./components/public/howitworks/howitworks.module').then(
                (m) => m.HowitworksModule,
              ),
          },
          {
            path: 'ricette',
            loadChildren: () =>
              import('./components/public/recipes/recipes.module').then((m) => m.RecipesModule),
          },
          {
            path: 'recipes',
            loadChildren: () =>
              import('./components/public/recipes/recipes.module').then((m) => m.RecipesModule),
          },
          {
            path: 'contatti',
            loadChildren: () =>
              import('./components/public/contacts/contacts.module').then((m) => m.ContactsModule),
          },
          {
            path: 'contacts',
            loadChildren: () =>
              import('./components/public/contacts/contacts.module').then((m) => m.ContactsModule),
          },
          {
            path: 'termini-e-condizioni',
            loadChildren: () =>
              import('./components/public/terms-and-conditions/terms-and-conditions.module').then(
                (m) => m.TermsAndConditionsModule,
              ),
          },
          {
            path: 'terms-and-conditions',
            loadChildren: () =>
              import('./components/public/terms-and-conditions/terms-and-conditions.module').then(
                (m) => m.TermsAndConditionsModule,
              ),
          },

          // NON CRITICHE PER SEO
          {
            path: t('request-password'),
            loadChildren: () =>
              import('./components/public/request-password/request-password.module').then(
                (m) => m.RequestPasswordModule,
              ),
          },
          {
            path: t('new-password') + '/verify',
            loadChildren: () =>
              import('./components/public/new-password/new-password.module').then(
                (m) => m.NewPasswordModule,
              ),
            data: { expectedQueryParams: ['token'] },
          },
          {
            path: 'privacy-policy',
            loadChildren: () =>
              import('./components/shared/privacy-policy/privacy-policy.module').then(
                (m) => m.PrivacyPolicyModule,
              ),
          },
          {
            path: 'cookie-policy',
            loadChildren: () =>
              import('./components/shared/cookie-policy/cookie-policy.module').then(
                (m) => m.CookiePolicyModule,
              ),
          },
          {
            path: 'stepper',
            component: StepperComponent,
            canActivate: [StepperGuard],
          },
        ],
      },
      // Sezione DASHBOARD (rimane dinamica con traduzioni)
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: { breadcrumb: 'Dashboard' },
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            redirectTo: t('ricerca-prodotti'),
            pathMatch: 'full',
          },
          {
            path: t('profilo-utente'),
            loadChildren: () =>
              import('./components/private/pages/user-profile/user-profile.module').then(
                (m) => m.UserProfileModule,
              ),
            data: { breadcrumb: 'profilo-utente', showBanner: false, bannerContent: null },
          },
          {
            path: t('lista-della-spesa'),
            loadChildren: () =>
              import('./components/private/pages/shopping-list/shopping-list.module').then(
                (m) => m.ShoppingListModule,
              ),
            data: { breadcrumb: 'lista-della-spesa', showBanner: false, bannerContent: null },
            canActivate: [RegistrationGuard],
          },
          {
            path: t('volantini'),
            loadChildren: () =>
              import('./components/private/pages/flyers/flyers.module').then((m) => m.FlyersModule),
            data: {
              breadcrumb: 'volantini',
              showBanner: false,
              bannerContent: null,
            },
            canActivate: [RegistrationGuard],
          },
          {
            path: t('wishlist'),
            loadChildren: () =>
              import('./components/private/pages/wishlist/wishlist.module').then(
                (m) => m.WishlistModule,
              ),
            data: {
              breadcrumb: 'wishlist',
              showBanner: false,
              bannerContent: null,
            },
            canActivate: [RegistrationGuard],
          },
          {
            path: t('ricerca-prodotti'),
            loadChildren: () =>
              import('./components/private/pages/research-products/research-product.module').then(
                (m) => m.ResearchProductModule,
              ),
            data: {
              breadcrumb: 'ricerca-prodotti',
              showBanner: false,
              bannerContent: null,
            },
            canActivate: [RegistrationGuard],
          },
          {
            path: t('le-mie-ricette'),
            loadChildren: () =>
              import('./components/private/pages/recipes/recipes.module').then(
                (m) => m.RecipesModule,
              ),
            data: { breadcrumb: 'le-mie-ricette', showBanner: false, bannerContent: null },
            canActivate: [RegistrationGuard],
          },
        ],
      },

      { path: '**', redirectTo: '' },
    ];
  } catch (err) {
    console.error(err);
    return [];
  }
};
